import { Component } from '@theme/component';
import { isClickedOutside, normalizeString, onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that displays a localization form.
 *
 * @typedef {object} FormRefs
 * @property {HTMLDivElement} countryList - The country list element.
 * @property {HTMLInputElement} countryInput - The country input element.
 * @property {HTMLUListElement[]} countryListItems - The country list items element.
 * @property {HTMLFormElement} form - The form element.
 * @property {HTMLDivElement} liveRegion - The live region element.
 * @property {HTMLSelectElement} languageInput - The language input element (hidden once the custom list is active).
 * @property {HTMLButtonElement} languageButton - The button that opens the custom language list.
 * @property {HTMLUListElement} languageList - The custom language listbox.
 * @property {HTMLLIElement[]} languageOptions - The options inside the custom language listbox.
 * @property {HTMLSpanElement} noResultsMessage - The no results message element.
 * @property {HTMLUListElement} popularCountries - The popular countries element.
 * @property {HTMLInputElement} search - The search input element.
 * @property {HTMLButtonElement} resetButton - The reset button element.
 *
 * @extends {Component<FormRefs>}
 */
class LocalizationFormComponent extends Component {
  /** Max height (px) of the custom language list. Keep in sync with the CSS max-height. */
  static LANGUAGE_LIST_MAX_HEIGHT = 288;

  /** The custom language list is desktop-only. Below this width the native select is used. Matches the theme's 750px breakpoint. */
  static LANGUAGE_LIST_MEDIA_QUERY = '(min-width: 750px)';

  /** @type {MediaQueryList | null} */
  #desktopMedia = null;

  #languageTypeahead = '';
  #languageTypeaheadTimer = 0;
  #languageEscapeHandled = false;

  connectedCallback() {
    super.connectedCallback();

    this.refs.search && this.refs.search.addEventListener('keydown', this.#onSearchKeyDown);
    this.refs.countryList && this.refs.countryList.addEventListener('keydown', this.#onContainerKeyDown);
    this.refs.countryList && this.refs.countryList.addEventListener('scroll', this.#onCountryListScroll);

    this.#setupLanguageList();

    // Resizing the language input can be expensive for browsers that don't support field-sizing: content.
    // Spliting it into separate tasks at least helps when there are multiple localization forms on the page.
    // (Skipped automatically when the custom language list is active, because the native select is hidden.)
    setTimeout(() => this.resizeLanguageInput(), 0);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.search && this.refs.search.removeEventListener('keydown', this.#onSearchKeyDown);
    this.refs.countryList && this.refs.countryList.removeEventListener('keydown', this.#onContainerKeyDown);
    this.refs.countryList && this.refs.countryList.removeEventListener('scroll', this.#onCountryListScroll);

    const languageSelector = this.#languageSelector;
    languageSelector?.removeEventListener('keydown', this.#onLanguageKeyDown);
    languageSelector?.removeEventListener('keyup', this.#onLanguageKeyUp);
    document.removeEventListener('click', this.#onDocumentClickForLanguage);
    this.#desktopMedia?.removeEventListener('change', this.#onLanguageBreakpointChange);
    clearTimeout(this.#languageTypeaheadTimer);
  }

  /**
   * Handles the keydown event for the container.
   *
   * @param {KeyboardEvent} event - The event object.
   */
  #onContainerKeyDown = (event) => {
    const { countryInput, countryListItems, form } = this.refs;

    switch (event.key) {
      case 'ArrowUp':
        event.preventDefault();
        event.stopPropagation();
        this.#changeCountryFocus('UP');
        break;
      case 'ArrowDown':
        event.preventDefault();
        event.stopPropagation();
        this.#changeCountryFocus('DOWN');
        break;
      case 'Enter': {
        event.preventDefault();
        event.stopPropagation();
        const focusedItem = countryListItems.find((item) => item.getAttribute('aria-selected') === 'true');

        if (focusedItem) {
          countryInput.value = focusedItem.dataset.value ?? '';
          form.submit();
        }
        break;
      }
    }

    if (!this.refs.search) return;

    setTimeout(() => {
      const focusableItems = this.refs.countryListItems.filter((item) => !item.hasAttribute('hidden'));
      const focusedItemIndex = focusableItems.findIndex((item) => item === document.activeElement);
      const focusedItem = focusableItems[focusedItemIndex];

      if (focusedItem) {
        this.refs.search.setAttribute('aria-activedescendant', focusedItem.id);
      } else {
        this.refs.search.setAttribute('aria-activedescendant', '');
      }
    });
  };

  /**
   * Selects a country.
   *
   * @param {string} countryName - The name of the country to select.
   * @param {Event} event - The event object.
   */
  selectCountry = (countryName, event) => {
    event.preventDefault();
    const { countryInput, form } = this.refs;

    countryInput.value = countryName;
    form?.submit();
  };

  /**
   * Changes the language of the localization form.
   * Still used as the fallback path when the custom language list is not active.
   *
   * @param {Event} event - The event object.
   */
  changeLanguage(event) {
    const { form, languageInput } = this.refs;
    const value = event.target instanceof HTMLSelectElement ? event.target.value : null;

    if (value) {
      languageInput.value = value;
      this.resizeLanguageInput();
      form.submit();
    }
  }

  resizeLanguageInput() {
    const { languageInput } = this.refs;

    if (!languageInput || CSS.supports('field-sizing', 'content')) return;

    // The native select is hidden when the custom language list is active, nothing to measure.
    if (this.#languageSelector?.hasAttribute('data-enhanced')) return;

    // Hide all options except the selected option
    for (const option of languageInput.options) {
      if (!option.selected) {
        option.dataset.optionLabel = option.textContent || '';
        option.innerText = '';
      }
    }

    // Calculate the width of the select element (which is based on the width of the widest option)
    languageInput.style.width = 'fit-content';
    const originalElementWidth = `${Math.ceil(languageInput.offsetWidth) + 1}px`;

    // Fix the width of the select element
    if (languageInput.offsetWidth > 0) {
      languageInput.style.width = originalElementWidth;
    }

    // Add back all option labels
    for (const option of languageInput.options) {
      if (option.dataset.optionLabel) {
        option.textContent = option.dataset.optionLabel;
        delete option.dataset.optionLabel;
      }
    }
  }

  /* ------------------------------------------------------------------ */
  /* Custom language list                                                */
  /* ------------------------------------------------------------------ */

  /**
   * @returns {HTMLElement | null} The language selector row.
   */
  get #languageSelector() {
    return this.querySelector('.language-selector');
  }

  /**
   * @returns {HTMLLIElement[]} The language options.
   */
  get #languageOptions() {
    return Array.from(this.refs.languageOptions ?? []);
  }

  /**
   * @returns {boolean} Whether the custom language list is open.
   */
  get #isLanguageListOpen() {
    const { languageList } = this.refs;
    return Boolean(languageList) && !languageList.hasAttribute('hidden');
  }

  /**
   * Sets up the custom language list. It is only active on desktop widths; on mobile the
   * native select is left untouched. The native select stays in the DOM in both cases and
   * remains the value that gets submitted with the form.
   */
  #setupLanguageList() {
    const { languageButton, languageList } = this.refs;
    const languageSelector = this.#languageSelector;

    if (!languageButton || !languageList || !languageSelector) return;

    languageSelector.addEventListener('keydown', this.#onLanguageKeyDown);
    languageSelector.addEventListener('keyup', this.#onLanguageKeyUp);

    this.#desktopMedia = window.matchMedia(LocalizationFormComponent.LANGUAGE_LIST_MEDIA_QUERY);
    this.#desktopMedia.addEventListener('change', this.#onLanguageBreakpointChange);
    this.#applyLanguageListMode();
  }

  /**
   * Turns the custom list on (desktop) or off (mobile) by toggling data-enhanced,
   * which the stylesheet uses to show either the custom picker or the native select.
   */
  #applyLanguageListMode() {
    const languageSelector = this.#languageSelector;

    if (!languageSelector || !this.#desktopMedia) return;

    if (this.#desktopMedia.matches) {
      languageSelector.setAttribute('data-enhanced', '');
    } else {
      this.#closeLanguageList();
      languageSelector.removeAttribute('data-enhanced');
    }
  }

  /**
   * Handles the viewport crossing the desktop breakpoint (e.g. window resize, device rotation).
   */
  #onLanguageBreakpointChange = () => {
    this.#applyLanguageListMode();

    // The native select is visible again, so it needs its width recalculated.
    if (!this.#desktopMedia?.matches) this.resizeLanguageInput();
  };

  /**
   * Toggles the language list.
   *
   * @param {Event} event - The event object.
   */
  toggleLanguageList(event) {
    event?.preventDefault();

    if (this.#isLanguageListOpen) {
      this.#closeLanguageList();
    } else {
      this.#openLanguageList('selected');
    }
  }

  /**
   * Picks a language from the custom list.
   *
   * @param {Event} event - The event object.
   */
  selectLanguage(event) {
    event.preventDefault();

    const option = event.target instanceof Element ? event.target.closest('[data-value]') : null;
    this.#chooseLanguage(option);
  }

  /**
   * Opens the language list and moves focus into it.
   *
   * @param {'selected' | 'first' | 'last'} focusTarget - Which option receives focus.
   */
  #openLanguageList(focusTarget = 'selected') {
    const { languageButton, languageList } = this.refs;
    const languageSelector = this.#languageSelector;

    if (!languageButton || !languageList || !languageSelector || this.#isLanguageListOpen) return;

    this.#positionLanguageList();

    languageList.removeAttribute('hidden');
    languageButton.setAttribute('aria-expanded', 'true');
    languageSelector.classList.add('is-open');
    document.addEventListener('click', this.#onDocumentClickForLanguage);

    const options = this.#languageOptions;
    const current = options.find((option) => option.getAttribute('aria-current') === 'true');
    let target = current ?? options[0];

    if (focusTarget === 'first') target = options[0];
    if (focusTarget === 'last') target = options[options.length - 1];

    if (!target) return;

    // Center the target row in the list, then focus without letting the browser scroll the page.
    languageList.scrollTop = target.offsetTop - languageList.clientHeight / 2 + target.offsetHeight / 2;
    target.focus({ preventScroll: true });
  }

  /**
   * Closes the language list.
   *
   * @param {boolean} [restoreFocus] - Whether focus should go back to the button.
   */
  #closeLanguageList(restoreFocus = false) {
    const { languageButton, languageList } = this.refs;

    if (!languageList || languageList.hasAttribute('hidden')) return;

    languageList.setAttribute('hidden', '');
    languageButton?.setAttribute('aria-expanded', 'false');
    this.#languageSelector?.classList.remove('is-open');
    document.removeEventListener('click', this.#onDocumentClickForLanguage);
    clearTimeout(this.#languageTypeaheadTimer);
    this.#languageTypeahead = '';

    if (restoreFocus) languageButton?.focus();
  }

  /**
   * Decides how tall the list may be and which side it opens on, so it never leaves its container.
   * When the country list is present, the language row is at the bottom of the panel, so the list
   * always opens upward and is limited to the room inside the panel.
   * When only the language selector is shown (e.g. footer), it opens on whichever side has more room.
   */
  #positionLanguageList() {
    const { languageList } = this.refs;
    const languageSelector = this.#languageSelector;

    if (!languageList || !languageSelector) return;

    const maxHeight = LocalizationFormComponent.LANGUAGE_LIST_MAX_HEIGHT;
    const selectorRect = languageSelector.getBoundingClientRect();
    const hasCountryList = languageSelector.classList.contains('language-selector--top-shadow');

    languageList.classList.remove('is-flipped');

    if (hasCountryList) {
      const roomInsidePanel = selectorRect.top - this.getBoundingClientRect().top - 8;
      languageList.style.maxHeight = `${Math.max(120, Math.min(maxHeight, roomInsidePanel))}px`;
      return;
    }

    const roomAbove = selectorRect.top - 16;
    const roomBelow = window.innerHeight - selectorRect.bottom - 16;
    const openBelow = roomBelow > roomAbove;

    languageList.classList.toggle('is-flipped', openBelow);
    languageList.style.maxHeight = `${Math.max(120, Math.min(maxHeight, openBelow ? roomBelow : roomAbove))}px`;
  }

  /**
   * Submits the form with the chosen language.
   *
   * @param {Element | null | undefined} option - The chosen option element.
   */
  #chooseLanguage(option) {
    const { form, languageInput } = this.refs;
    const value = option instanceof HTMLElement ? option.dataset.value : null;

    if (!value) return;

    // Same language picked again: just close, no need to reload the page.
    if (option.getAttribute('aria-current') === 'true') {
      this.#closeLanguageList(true);
      return;
    }

    languageInput.value = value;
    form.submit();
  }

  /**
   * Closes the list when the user clicks outside the language selector.
   *
   * @param {MouseEvent} event - The event object.
   */
  #onDocumentClickForLanguage = (event) => {
    const languageSelector = this.#languageSelector;

    if (languageSelector && event.target instanceof Node && !languageSelector.contains(event.target)) {
      this.#closeLanguageList();
    }
  };

  /**
   * Keyboard support for the button and the listbox.
   *
   * @param {KeyboardEvent} event - The event object.
   */
  #onLanguageKeyDown = (event) => {
    const { languageButton } = this.refs;
    const options = this.#languageOptions;

    if (!this.#isLanguageListOpen) {
      if (event.target === languageButton && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
        event.preventDefault();
        event.stopPropagation();
        this.#openLanguageList(event.key === 'ArrowUp' ? 'last' : 'selected');
      }
      return;
    }

    const focusedIndex = options.findIndex((option) => option === document.activeElement);

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        event.stopPropagation();
        options[focusedIndex < options.length - 1 ? focusedIndex + 1 : 0]?.focus();
        break;
      case 'ArrowUp':
        event.preventDefault();
        event.stopPropagation();
        options[focusedIndex > 0 ? focusedIndex - 1 : options.length - 1]?.focus();
        break;
      case 'Home':
        event.preventDefault();
        options[0]?.focus();
        break;
      case 'End':
        event.preventDefault();
        options[options.length - 1]?.focus();
        break;
      case 'Enter':
      case ' ':
        if (focusedIndex >= 0) {
          event.preventDefault();
          event.stopPropagation();
          this.#chooseLanguage(options[focusedIndex]);
        }
        break;
      case 'Escape':
        // Close only the list, not the whole panel. The matching keyup is swallowed in #onLanguageKeyUp.
        event.preventDefault();
        event.stopPropagation();
        this.#languageEscapeHandled = true;
        this.#closeLanguageList(true);
        break;
      case 'Tab':
        this.#closeLanguageList();
        break;
      default:
        this.#focusLanguageByTyping(event, options, focusedIndex);
    }
  };

  /**
   * Swallows the Escape keyup that belongs to a keydown already used to close the list,
   * so the dropdown panel doesn't close as well.
   *
   * @param {KeyboardEvent} event - The event object.
   */
  #onLanguageKeyUp = (event) => {
    if (event.key === 'Escape' && this.#languageEscapeHandled) {
      event.stopPropagation();
      this.#languageEscapeHandled = false;
    }
  };

  /**
   * Type-ahead: typing letters jumps to the matching language.
   *
   * @param {KeyboardEvent} event - The event object.
   * @param {HTMLLIElement[]} options - The language options.
   * @param {number} focusedIndex - Index of the currently focused option.
   */
  #focusLanguageByTyping(event, options, focusedIndex) {
    if (event.key.length !== 1 || event.ctrlKey || event.metaKey || event.altKey) return;

    event.preventDefault();

    clearTimeout(this.#languageTypeaheadTimer);
    this.#languageTypeahead += normalizeString(event.key);
    this.#languageTypeaheadTimer = window.setTimeout(() => {
      this.#languageTypeahead = '';
    }, 600);

    // A single letter moves to the next match; a longer string narrows the current one.
    const startIndex = this.#languageTypeahead.length === 1 ? focusedIndex + 1 : Math.max(focusedIndex, 0);
    const ordered = [...options.slice(startIndex), ...options.slice(0, startIndex)];
    const match = ordered.find((option) =>
      normalizeString(option.querySelector('.country')?.textContent ?? '').startsWith(this.#languageTypeahead)
    );

    match?.focus();
  }

  /* ------------------------------------------------------------------ */
  /* Country search                                                      */
  /* ------------------------------------------------------------------ */

  /**
   * Finds matches for a given search value in a country element.
   *
   * @typedef {Object} Options
   * @property {boolean} [matchLabel] - Whether to match the label.
   * @property {boolean} [matchAlias] - Whether to match the alias.
   * @property {boolean} [matchIso] - Whether to match the iso.
   * @property {boolean} [matchCurrency] - Whether to match the currency.
   * @property {boolean} [labelMatchStart] - Whether to match the label start.
   * @property {boolean} [aliasExactMatch] - Whether to match the alias exact match.
   *
   * @typedef {Object} MatchTypes
   * @property {boolean} [label] - Whether the label matches the search value.
   * @property {boolean} [alias] - Whether the alias matches the search value.
   * @property {boolean} [iso] - Whether the iso matches the search value.
   * @property {boolean} [currency] - Whether the currency matches the search value.
   *
   * @param {string} searchValue - The search value to find matches for.
   * @param {HTMLElement} countryEl - The country element to find matches in.
   * @param {Options} options - The options for the search.
   * @returns {MatchTypes} The matches found in the country element.
   */
  #findMatches(
    searchValue,
    countryEl,
    options = {
      // Which data types (label, alias, iso) to match against
      matchLabel: true,
      matchAlias: true,
      matchIso: true,
      matchCurrency: true,
      // If true, the search value must match the start of the label
      labelMatchStart: false,
      // If true, a result will not display unless the search value equals an alias in its entirety
      aliasExactMatch: false,
    }
  ) {
    let matchTypes = {};
    const { aliases, value: iso } = countryEl.dataset;

    if (options.matchLabel) {
      const countryName = normalizeString(countryEl.querySelector('.country')?.textContent ?? '');

      if (!countryName) return matchTypes;

      matchTypes.label = options.labelMatchStart
        ? countryName.startsWith(searchValue)
        : countryName.includes(searchValue);
    }

    if (options.matchCurrency) {
      const currency = normalizeString(countryEl.querySelector('.localization-form__currency')?.textContent ?? '');
      matchTypes.currency = currency.includes(searchValue);
    }

    if (options.matchIso) {
      matchTypes.iso = normalizeString(iso ?? '') == searchValue;
    }

    if (options.matchAlias) {
      const countryAliases = aliases?.split(',').map((alias) => normalizeString(alias));

      if (!countryAliases) return matchTypes;

      matchTypes.alias =
        countryAliases.length > 0 &&
        countryAliases.find((alias) =>
          options.aliasExactMatch ? alias === searchValue : alias.startsWith(searchValue)
        ) !== undefined;
    }

    return matchTypes;
  }

  /**
   * Highlights matching text in a string by wrapping it in <mark> tags.
   *
   * @param {string | null} text - The text to highlight.
   * @param {string} searchValue - The search value to highlight.
   * @returns {string} The text with matching parts wrapped in <mark> tags.
   */
  #highlightMatches(text, searchValue) {
    if (!text || !searchValue) return text ?? '';

    const normalizedText = normalizeString(text);
    const normalizedSearch = normalizeString(searchValue);
    const startIndex = normalizedText.indexOf(normalizedSearch);

    if (startIndex === -1) return text;

    const endIndex = startIndex + normalizedSearch.length;
    const before = text.slice(0, startIndex);
    const match = text.slice(startIndex, endIndex);
    const after = text.slice(endIndex);

    let result = '';
    if (before) {
      result += `<mark>${before}</mark>`;
    }
    result += match;
    if (after) {
      result += `<mark>${after}</mark>`;
    }
    return result;
  }

  /**
   * Filters the countries based on the search value.
   */
  filterCountries() {
    const { countryList, countryListItems, liveRegion, noResultsMessage, popularCountries, resetButton, search } =
      this.refs;
    const { labelResultsCount } = this.dataset;
    const searchValue = normalizeString(search.value);
    let countVisibleCountries = 0;

    resetButton.toggleAttribute('hidden', !searchValue);

    const wrapper = this.querySelector('.country-selector-form__wrapper');
    if (wrapper) {
      wrapper.classList.toggle('is-searching', !!searchValue);
    }

    for (const countryEl of countryListItems) {
      if (searchValue === '') {
        countryEl.removeAttribute('hidden');
        const countrySpan = countryEl.querySelector('.country');
        if (countrySpan) {
          // eslint-disable-next-line no-self-assign
          countrySpan.textContent = countrySpan.textContent;
        }
        countVisibleCountries++;
      } else {
        const matches = this.#findMatches(searchValue, countryEl);

        // In the future, we could reorder/rank filtered results based on the match types
        if (matches.label || matches.alias || matches.iso || matches.currency) {
          countryEl.removeAttribute('hidden');
          const countrySpan = countryEl.querySelector('.country');
          if (countrySpan) {
            countrySpan.innerHTML = this.#highlightMatches(countrySpan.textContent, searchValue);
          }
          countVisibleCountries++;
        } else {
          countryEl.setAttribute('hidden', '');
        }
      }
    }

    // Popular countries are only listed in their own list, so it stays visible while it still has a match.
    if (popularCountries) {
      const hasVisiblePopularCountry = Array.from(popularCountries.children).some(
        (country) => !country.hasAttribute('hidden')
      );

      popularCountries.toggleAttribute('hidden', !hasVisiblePopularCountry);
    }

    if (liveRegion && labelResultsCount) {
      liveRegion.innerText = labelResultsCount.replace('[count]', `${countVisibleCountries}`);
    }

    noResultsMessage.hidden = countVisibleCountries > 0;
    countryList.scrollTop = 0;
  }

  /**
   * Changes the focus of the country list items.
   *
   * @param {string} direction - The direction to change the focus.
   */
  #changeCountryFocus(direction) {
    const { countryListItems } = this.refs;
    const focusableItems = countryListItems.filter((item) => !item.hasAttribute('hidden'));
    const focusedItemIndex = focusableItems.findIndex((item) => item === document.activeElement);
    const focusedItem = focusableItems[focusedItemIndex];
    let itemToFocus;

    if (direction === 'UP') {
      itemToFocus =
        focusedItemIndex > 0 ? focusableItems[focusedItemIndex - 1] : focusableItems[focusableItems.length - 1];
    } else {
      itemToFocus =
        focusedItemIndex < focusableItems.length - 1 ? focusableItems[focusedItemIndex + 1] : focusableItems[0];
    }

    if (focusedItem) {
      focusedItem.setAttribute('aria-selected', 'false');
    }
    itemToFocus?.setAttribute('aria-selected', 'true');
    itemToFocus?.focus();
  }

  /**
   * Resets the countries filter.
   *
   * @param {Event} event - The event object.
   */
  resetCountriesFilter(event) {
    const { search } = this.refs;

    event.stopPropagation();
    search.value = '';
    this.filterCountries();
    search.setAttribute('aria-activedescendant', '');
    search.focus();
  }

  /**
   * Handles the keydown event for the search input.
   *
   * @param {KeyboardEvent} event - The event object.
   */
  #onSearchKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    this.#onContainerKeyDown(event);
  };

  /**
   * Resets the form.
   */
  resetForm() {
    // Panel/drawer is closing: make sure the language list doesn't stay open behind it.
    this.#closeLanguageList();

    const { search } = this.refs;

    if (!search) return;

    if (search.value != '') {
      search.value = '';
      this.filterCountries();
      search.setAttribute('aria-activedescendant', '');
    }
  }

  /**
   * Focuses the search input.
   */
  focusSearchInput = () => {
    const { search } = this.refs;

    search?.focus();
  };

  /**
   * Handles the scroll event on the country list.
   *
   * @param {Event} event - The scroll event object.
   */
  #onCountryListScroll = (event) => {
    const countryFilter = this.querySelector('.country-filter');
    const countryList = event.target instanceof HTMLElement ? event.target : null;

    if (countryFilter && countryList) {
      const shouldShowBorder = countryList.scrollTop > 0;
      countryFilter.classList.toggle('is-scrolled', shouldShowBorder);
    }
  };
}

/**
 * A custom element that displays a dropdown localization form.
 *
 * @typedef {object} DropdownRefs
 * @property {HTMLButtonElement} button - The button element.
 * @property {HTMLDivElement} panel - The panel element.
 * @property {LocalizationFormComponent} localizationForm - The localization form component.
 *
 * @extends {Component<DropdownRefs>}
 */
class DropdownLocalizationComponent extends Component {
  get isHidden() {
    return this.refs.panel.hasAttribute('hidden');
  }

  /**
   * Toggles the panel.
   */
  toggleSelector() {
    return this.isHidden ? this.showPanel() : this.hidePanel();
  }

  /**
   * Shows the panel.
   */
  showPanel() {
    if (!this.isHidden) return;

    this.addEventListener('keyup', this.#handleKeyUp);
    document.addEventListener('click', this.#handleClickOutside);

    this.refs.panel.removeAttribute('hidden');
    this.refs.button.setAttribute('aria-expanded', 'true');

    onAnimationEnd(this.refs.panel, () => {
      this.#updateWidth();
      this.refs.localizationForm?.focusSearchInput();
    });
  }

  /**
   * Hides the panel.
   */
  hidePanel = () => {
    if (this.isHidden) return;

    this.removeEventListener('keyup', this.#handleKeyUp);
    document.removeEventListener('click', this.#handleClickOutside);

    this.refs.button?.setAttribute('aria-expanded', 'false');
    this.refs.panel.setAttribute('hidden', '');
    this.refs.localizationForm?.resetForm();
  };

  /**
   * Handles the click outside event.
   *
   * @param {PointerEvent} event - The event object.
   */
  #handleClickOutside = (event) => {
    if (isClickedOutside(event, this)) {
      this.hidePanel();
    }
  };

  /**
   * Updates the width of the panel.
   */
  #updateWidth() {
    this.style.setProperty('--width', `${this.refs.localizationForm.offsetWidth}px`);
  }

  /**
   * Handles the keyup event.
   *
   * @param {KeyboardEvent} event - The event object.
   */
  #handleKeyUp = (event) => {
    switch (event.key) {
      case 'Escape':
        this.hidePanel();
        event.stopPropagation();
        this.refs.button?.focus();
        break;
    }
  };
}

/**
 * A custom element that displays a drawer localization form.
 *
 * @typedef {object} DrawerRefs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {LocalizationFormComponent} localizationForm - The localization form component.
 *
 * @extends {Component<DrawerRefs>}
 */
class DrawerLocalizationComponent extends Component {
  /**
   * Toggles the dialog.
   *
   * @param {ToggleEvent} event - The event object.
   */
  toggle(event) {
    const { target } = event;
    const { localizationForm } = this.refs;

    if (!localizationForm || !(target instanceof HTMLDetailsElement)) return;

    const countryList = localizationForm.querySelector('.country-selector-form__wrapper');

    if (target.open) {
      if (countryList) countryList.addEventListener('scroll', this.#onCountryListScroll);
      onAnimationEnd(target, localizationForm.focusSearchInput);
    } else {
      countryList?.removeEventListener('scroll', this.#onCountryListScroll);
      localizationForm.resetForm();
    }
  }

  /**
   * Handles the scroll event on the country list.
   *
   * @param {Event} event - The scroll event object.
   */
  #onCountryListScroll = (event) => {
    const countryFilter = this.querySelector('.country-filter');
    const countryList = event.target instanceof HTMLElement ? event.target : null;

    if (countryFilter && countryList) {
      const shouldShowBorder = countryList.scrollTop > 0;
      countryFilter.classList.toggle('is-scrolled', shouldShowBorder);
    }
  };
}

if (!customElements.get('localization-form-component')) {
  customElements.define('localization-form-component', LocalizationFormComponent);
}

if (!customElements.get('dropdown-localization-component')) {
  customElements.define('dropdown-localization-component', DropdownLocalizationComponent);
}

if (!customElements.get('drawer-localization-component')) {
  customElements.define('drawer-localization-component', DrawerLocalizationComponent);
}